import { Input, Text } from "@solomon-labs/design/components/ui";
import React, { useState } from "react";

import { RewardsDestinationField } from "./RewardsDestinationField";

interface LabeledRewardsDestinationProps {
    label: string;
    onLabelChange: (value: string) => void;
    address: string;
    onAddressChange: (value: string) => void;
    /** Connected wallet address — feeds the address field's placeholder
     *  ("looks pre-filled") and the empty-as-default semantic on submit. */
    connectedWallet: string;
    /** Label input placeholder. Defaults to "Account label". */
    labelPlaceholder?: string;
    /** When false, hides the "Rewards Destination" title rendered
     *  above BOTH the label input and the
     *  address input (the title covers the whole destination group,
     *  not just one input). Per-position editors that already identify
     *  the field via surrounding context pass false; the apply form
     *  uses the default. */
    showDestinationLabel?: boolean;
    /** Override for the address input placeholder. When omitted, the
     *  field shows the connected wallet as placeholder (its default —
     *  reads as a prefilled hint). */
    addressPlaceholder?: string;
    /** Hide the inline on-chain status line — defaults true since both
     *  current consumers surface verification in their own layout slot
     *  (apply form: reserved status line below; inline editor: status
     *  shares a row with the Verify CTA). */
    hideOnChainStatus?: boolean;
    /** Enrollment supplies its wallet-type and signer verification here. */
    verificationContent?: React.ReactNode;
}

/**
 * Paired Account label + Rewards Destination address inputs. Used by
 * the Enable Rewards form (initial enrollment) and InlineDestinationEditor
 * (per-position Mode 2 routing edit) — same visual pattern in both.
 *
 * Owns the label-input focus state internally (purely visual
 * placeholder swap); the actual label + address values are controlled
 * by the parent so it can wire validation, prefill, and submit.
 *
 * The address field is wrapped in a min-h-11 div that floors the
 * empty-field height. The AccountTypeBadge renders on its own line
 * above the input (see RewardsDestinationField) so the address gets the
 * full field width; when the badge resolves the field grows by the
 * badge's height — a one-time settle rather than an inline reflow.
 */
export function LabeledRewardsDestination({
    label,
    onLabelChange,
    address,
    onAddressChange,
    connectedWallet,
    labelPlaceholder = "Account label",
    showDestinationLabel = true,
    addressPlaceholder,
    hideOnChainStatus = true,
    verificationContent
}: LabeledRewardsDestinationProps): React.ReactElement {
    const [labelFocused, setLabelFocused] = useState(false);
    return (
        <div className="flex flex-col gap-3">
            {showDestinationLabel && (
                <Text
                    variant="body"
                    render={<span />}
                    className="text-foreground-base"
                >
                    Rewards Destination
                </Text>
            )}
            <div className="flex flex-col gap-2">
                {/* DS Input rather than a hand-assembled
                    font/border/focus cluster. This file predates the
                    main app consuming @solomon-labs/design ui
                    primitives; the `small` text variant, placeholder
                    colour and focus treatment are all baked into
                    `Input` now. */}
                <Input
                    interaction="lightbar"
                    size="md"
                    aria-label={labelPlaceholder}
                    value={label}
                    onChange={(e) => onLabelChange(e.target.value)}
                    onFocus={() => setLabelFocused(true)}
                    onBlur={() => setLabelFocused(false)}
                    placeholder={labelFocused ? "" : labelPlaceholder}
                />
                <div className="min-h-11">
                    <RewardsDestinationField
                        value={address}
                        onChange={onAddressChange}
                        state="default"
                        connectedWallet={connectedWallet}
                        showLabel={false}
                        placeholder={addressPlaceholder}
                        hideOnChainStatus={hideOnChainStatus}
                        verificationContent={verificationContent}
                    />
                </div>
            </div>
        </div>
    );
}
